import axios, { getData } from 'lib/axios'

// Location cache to reduce API calls
const locationCache = new Map()
const CACHE_DURATION = 5 * 60 * 1000 // 5 minutes in milliseconds
const CACHE_PRECISION = 0.0001 // ~11 meters precision for caching

// TomTom API configuration (using backend proxy)
const TOMTOM_DAILY_LIMIT = 2500
const tomtomUsageKey = `tomtom_usage_${new Date().toDateString()}`

const safeStorageGet = (key, fallback = '0') => {
  try {
    if (typeof window === 'undefined' || !window.localStorage) return fallback
    return window.localStorage.getItem(key) || fallback
  } catch {
    return fallback
  }
}
const safeStorageSet = (key, value) => {
  try {
    if (typeof window === 'undefined' || !window.localStorage) return
    window.localStorage.setItem(key, value)
  } catch {
    // ignore
  }
}

let tomtomCallsToday = parseInt(safeStorageGet(tomtomUsageKey, '0'), 10)

// Address accuracy: TomTom
const DEFAULT_GEOCODE_PROVIDER = 'tomtom'
// Map image rendering: OSM
const DEFAULT_MAP_PROVIDER = 'openstreetmap'

// Backend proxy configuration
const BACKEND_GEOCODING_AVAILABLE = true // Backend proxy handles API keys and CORS

/**
 * Generates a cache key based on rounded coordinates
 */
const getCacheKey = (latitude, longitude) => {
  const roundedLat = Math.round(latitude / CACHE_PRECISION) * CACHE_PRECISION
  const roundedLon = Math.round(longitude / CACHE_PRECISION) * CACHE_PRECISION
  return `${roundedLat},${roundedLon}`
}

/**
 * Checks if cached location data is still valid
 */
const isCacheValid = (cachedData) => {
  return cachedData && (Date.now() - cachedData.timestamp) < CACHE_DURATION
}

const getLocation = async (latitude, longitude, options = {}) => {
  try {
    const cacheKey = getCacheKey(latitude, longitude)
    const cached = locationCache.get(cacheKey)
    if (isCacheValid(cached)) return cached.data

    const provider = (options.provider || DEFAULT_GEOCODE_PROVIDER).toLowerCase()

    let location = null

    if (provider === 'tomtom') {
      location = await fetchTomTomLocation(latitude, longitude)
    } else {
      // If you still want to allow nominatim explicitly:
      location = await fetchNominatimLocation(latitude, longitude)
    }

    locationCache.set(cacheKey, { data: location, timestamp: Date.now() })
    if (locationCache.size > 100) cleanupCache()

    return location
  } catch (error) {
    throw new Error(`Failed to fetch location: ${error?.message || String(error)}`)
  }
}

/**
 * Cleans up expired cache entries
 */
const cleanupCache = () => {
  const now = Date.now()
  for (const [key, value] of locationCache.entries()) {
    if (now - value.timestamp > CACHE_DURATION) {
      locationCache.delete(key)
    }
  }
}



const toNumberOr = (v, fallback) => {
  const n = Number(v)
  return Number.isFinite(n) ? n : fallback
}

const normalizeMapResponse = (result, { provider, width, height, zoom }) => {
  if (!result?.success || !result?.data) {
    throw new Error(result?.message || 'Map request failed')
  }

  const payload = result.data

  return {
    success: true,
    imageData: payload.image_data ?? payload.imageData,
    mimeType: payload.mime_type ?? payload.mimeType ?? 'image/png',
    width: toNumberOr(payload.width, width),
    height: toNumberOr(payload.height, height),
    zoom: toNumberOr(payload.zoom, zoom),
    source: provider,
  }
}

const DEFAULT_PROVIDER = 'openstreetmap'

/**
 * Fetches location data from TomTom Reverse Geocoding API via backend proxy
 */
const fetchTomTomLocation = async (latitude, longitude) => {
  const response = await axios.post(
    '/geocoding/tomtom/reverse',
    {
      lat: latitude,
      lon: longitude,
      radius: 100,
      language: 'en-US',
      entityTypeSet: 'Address,POI,Geography,Building',
      returnMatchType: 'true',
      returnGeometries: 'true',
      returnPoi: 'true',
      returnAddress: 'true',
      returnPoiCategories: 'true',
      returnPoiBrands: 'true',
      maxResults: 10,
    },
    { timeout: 15000 }
  )

  // usage tracking
  tomtomCallsToday++
  safeStorageSet(tomtomUsageKey, String(tomtomCallsToday))

  const result = getData(response)

  if (!result.success || !result.data?.addresses || result.data.addresses.length === 0) {
    throw new Error(result.message || 'No results from TomTom API')
  }

  // pick best address (keep your scoring if you want; here keep simplest)
  let bestAddress = result.data.addresses[0]



  const enhancedData = enhanceTomTomLocationData(bestAddress)

  // normalize fields the UI uses
  enhancedData.backend_source = 'proxy'
  enhancedData.api_response = result
  enhancedData.total_results = result.data.addresses.length
  enhancedData.source = 'tomtom' // ✅ enforce

  return enhancedData
}

/**
 * Fetches location data from Nominatim with enhanced parameters
 */
const fetchNominatimLocation = async (latitude, longitude) => {
  const response = await axios.post(
    '/geocoding/nominatim/reverse',
    { lat: latitude, lon: longitude },
    { timeout: 10000 }
  )

  const result = getData(response)
  if (!result.success || !result.data) {
    throw new Error(result.message || 'No results from Nominatim')
  }

  const enhanced = enhanceLocationData(result.data)
  enhanced.backend_source = 'proxy'
  enhanced.source = 'openstreetmap'
  return enhanced
}

/**
 * Fallback geocoding using multiple services for better building name detection
 */
const fetchFallbackLocation = async (latitude, longitude) => {
  // Try BigDataCloud API (free alternative with good building data)
  try {
    const response = await axios.get('https://api.bigdatacloud.net/data/reverse-geocode-client', {
      params: {
        latitude: latitude,
        longitude: longitude,
        localityLanguage: 'en'
      },
      timeout: 5000 // 5 second timeout for fallback
    })

    const data = response.data
    return {
      building_name: data.localityInfo?.informative?.find(item =>
        item.description?.toLowerCase().includes('building') ||
        item.description?.toLowerCase().includes('office') ||
        item.description?.toLowerCase().includes('mall') ||
        item.description?.toLowerCase().includes('center')
      )?.name,
      display_name: data.locality,
      accuracy_level: 'building'
    }
  } catch (bigDataError) {
    // Try LocationIQ API as second fallback (requires API key but has good coverage)
    console.warn('BigDataCloud fallback failed:', bigDataError.message)

    // For production, you would add your LocationIQ API key here
    // const LOCATIONIQ_API_KEY = process.env.REACT_APP_LOCATIONIQ_API_KEY
    // if (LOCATIONIQ_API_KEY) {
    //   const response = await axios.get('https://us1.locationiq.com/v1/reverse.php', {
    //     params: {
    //       key: LOCATIONIQ_API_KEY,
    //       lat: latitude,
    //       lon: longitude,
    //       format: 'json',
    //       addressdetails: 1,
    //       extratags: 1,
    //       zoom: 18
    //     },
    //     timeout: 5000
    //   })
    //   return enhanceLocationData(response.data)
    // }

    throw new Error('All fallback services failed')
  }
}

/**
 * Merges fallback geocoding data with primary Nominatim data
 */
const mergeFallbackData = (primaryData, fallbackData) => {
  return {
    ...primaryData,
    building_name: fallbackData.building_name || primaryData.building_name,
    display_name: fallbackData.building_name ?
      `${fallbackData.building_name}, ${primaryData.display_name}` :
      primaryData.display_name,
    formatted_address: fallbackData.building_name ?
      `${fallbackData.building_name}, ${primaryData.formatted_address}` :
      primaryData.formatted_address,
    fallback_used: true,
    fallback_source: 'bigdatacloud'
  }
}

/**
 * Enhances TomTom location data into our standard format
 * @param {Object} tomtomData - Raw TomTom response data
 * @returns {Object} Enhanced location data with building information
 */
const enhanceTomTomLocationData = (tomtomData) => {
  if (!tomtomData) return null

  const { address, poi } = tomtomData

  const resolvedPosition = (() => {
    const raw = tomtomData?.position
    if (!raw || typeof raw !== 'string') return null

    const [latStr, lonStr] = raw.split(',').map((s) => s.trim())
    const lat = Number(latStr)
    const lon = Number(lonStr)

    if (!Number.isFinite(lat) || !Number.isFinite(lon)) return null
    return { lat, lon }
  })()

  // Enhanced building name extraction with comprehensive TomTom data sources
  const buildingName = (() => {
    return (
      poi?.name ||
      poi?.brands?.[0]?.name ||
      address?.buildingName ||
      address?.building ||
      (address?.freeformAddress &&
        (address.freeformAddress.includes('Building') ||
          address.freeformAddress.includes('Tower') ||
          address.freeformAddress.includes('Plaza') ||
          address.freeformAddress.includes('Center') ||
          address.freeformAddress.includes('Mall') ||
          address.freeformAddress.includes('Office') ||
          address.freeformAddress.includes('Residential') ||
          address.freeformAddress.includes('Complex') ||
          address.freeformAddress.includes('Square') ||
          address.freeformAddress.includes('Block') ||
          address.freeformAddress.includes('Suite') ||
          address.freeformAddress.includes('Floor'))
        ? address.freeformAddress.split(',')[0]
        : null) ||
      (address?.streetNumber && address?.streetName
        ? `${address.streetNumber} ${address.streetName}`
        : null) ||
      (poi?.categories?.[0] && poi.categories[0].toLowerCase().includes('building')
        ? poi.categories[0]
        : null) ||
      null
    )
  })()

  const addressComponents = {
    building: buildingName,
    house_number: address?.streetNumber,
    road: address?.streetName,
    neighbourhood: address?.localName,
    suburb: address?.neighbourhood || address?.municipalitySubdivision,
    city: address?.municipality,
    state: address?.countrySubdivision,
    country: address?.country,
    postcode: address?.postalCode,
  }

  const locationPriority = [
    address?.municipalitySubdivision,
    address?.neighbourhood,
    address?.localName,
    address?.residential,
    address?.commercial,
    address?.municipality,
    address?.city,
    address?.town,
    address?.village,
    address?.streetName,
  ].filter(Boolean)

  let primaryLocation = locationPriority[0] || address?.freeformAddress

  if (!primaryLocation || primaryLocation.includes('Jalan')) {
    const freeformAddress = address?.freeformAddress || ''
    const areaPatterns = [
      /Kota Damansara/i,
      /Mont Kiara/i,
      /Bangsar/i,
      /Taman [A-Za-z\s]+/i,
      /Kampung [A-Za-z\s]+/i,
      /Desa [A-Za-z\s]+/i,
      /Petaling Jaya/i,
      /Subang Jaya/i,
      /Shah Alam/i,
    ]

    for (const pattern of areaPatterns) {
      const match = freeformAddress.match(pattern)
      if (match) {
        primaryLocation = match[0]
        break
      }
    }
  }

  const displayParts = [
    buildingName,
    primaryLocation,
    address?.municipality && address?.municipality !== primaryLocation ? address.municipality : null,
    address?.countrySubdivision,
    address?.postalCode,
  ].filter(Boolean)

  const enhancedDisplayName = displayParts.join(', ')

  const formattedAddress =
    address?.freeformAddress ||
    [
      buildingName,
      address?.streetNumber && address?.streetName && primaryLocation !== address?.streetName
        ? `${address.streetNumber} ${address.streetName}`
        : null,
      primaryLocation,
      address?.municipality && address?.municipality !== primaryLocation ? address.municipality : null,
      address?.countrySubdivision,
    ]
      .filter(Boolean)
      .join(', ')

  let accuracyLevel = 'area'
  if (buildingName && poi?.name) accuracyLevel = 'building'
  else if (buildingName) accuracyLevel = 'address'
  else if (address?.streetNumber && primaryLocation) accuracyLevel = 'address'
  else if (primaryLocation && !primaryLocation.includes('Jalan')) accuracyLevel = 'neighborhood'
  else if (address?.streetName) accuracyLevel = 'street'
  else if (address?.municipality) accuracyLevel = 'city'

  return {
    display_name: enhancedDisplayName,
    formatted_address: formattedAddress,
    building_name: buildingName,
    primary_location: primaryLocation,
    address_components: addressComponents,
    location_type: getTomTomBuildingType(poi, address),
    accuracy_level: accuracyLevel,
    source: 'tomtom',

    resolved_latitude: resolvedPosition?.lat ?? null,
    resolved_longitude: resolvedPosition?.lon ?? null,
    resolved_position_raw: tomtomData?.position ?? null,

    raw_data: tomtomData,
  }
}


/**
 * Determines the building type from TomTom POI data
 * @param {Object} poi - TomTom POI data
 * @param {Object} address - TomTom address data
 * @returns {string} Building/location type
 */
const getTomTomBuildingType = (poi, address) => {
  if (poi?.categories && poi.categories.length > 0) {
    // TomTom categories are quite detailed, map to our simplified types
    const category = poi.categories[0].toLowerCase()

    if (category.includes('restaurant') || category.includes('food')) return 'restaurant'
    if (category.includes('hotel') || category.includes('accommodation')) return 'hotel'
    if (category.includes('hospital') || category.includes('medical')) return 'hospital'
    if (category.includes('school') || category.includes('education')) return 'school'
    if (category.includes('shopping') || category.includes('retail')) return 'shop'
    if (category.includes('office') || category.includes('business')) return 'office'
    if (category.includes('bank') || category.includes('atm')) return 'bank'
    if (category.includes('gas') || category.includes('petrol')) return 'fuel'

    return category
  }

  return 'building'
}

/**
 * Enhances location data by extracting building names and creating a better display name
 * @param {Object} locationData - Raw Nominatim response data
 * @returns {Object} Enhanced location data with building information
 */
const enhanceLocationData = (locationData) => {
  if (!locationData) return locationData

  const { address, extratags, namedetails } = locationData

  const buildingName =
    extratags?.name ||
    extratags?.official_name ||
    extratags?.['name:en'] ||
    extratags?.building ||
    extratags?.['building:name'] ||
    extratags?.['name:ms'] ||
    namedetails?.name ||
    namedetails?.['name:en'] ||
    namedetails?.['name:ms'] ||
    address?.amenity ||
    address?.building ||
    address?.office ||
    address?.shop ||
    address?.restaurant ||
    address?.hotel ||
    address?.hospital ||
    address?.school ||
    address?.university ||
    address?.college ||
    address?.bank ||
    address?.fuel ||
    address?.pharmacy ||
    address?.clinic ||
    (address?.house_number && address?.road ? `${address.house_number} ${address.road}` : null) ||
    (locationData?.display_name &&
      (locationData.display_name.includes('Building') ||
        locationData.display_name.includes('Tower') ||
        locationData.display_name.includes('Plaza') ||
        locationData.display_name.includes('Center') ||
        locationData.display_name.includes('Mall') ||
        locationData.display_name.includes('Office') ||
        locationData.display_name.includes('Residential'))
      ? locationData.display_name.split(',')[0]
      : null) ||
    null

  const locationPriority = [
    address?.neighbourhood,
    address?.suburb,
    address?.residential,
    address?.commercial,
    address?.hamlet,
    address?.quarter,
    address?.city || address?.town || address?.village,
    address?.municipality,
    address?.county,
    address?.road,
  ].filter(Boolean)

  let primaryLocation = locationPriority[0] || null

  if (!primaryLocation || primaryLocation.includes('Jalan')) {
    const displayName = locationData?.display_name || ''
    const areaPatterns = [
      /Kota Damansara/i,
      /Mont Kiara/i,
      /Bangsar/i,
      /Taman [A-Za-z\s]+/i,
      /Kampung [A-Za-z\s]+/i,
      /Desa [A-Za-z\s]+/i,
      /Petaling Jaya/i,
      /Subang Jaya/i,
      /Shah Alam/i,
    ]

    for (const pattern of areaPatterns) {
      const match = displayName.match(pattern)
      if (match) {
        primaryLocation = match[0]
        break
      }
    }
  }

  const displayParts = [
    buildingName,
    primaryLocation,
    address?.city && address?.city !== primaryLocation ? address.city : null,
    address?.state,
    address?.postcode,
  ].filter(Boolean)

  const enhancedDisplayName = displayParts.join(', ')

  const addressComponents = {
    building: buildingName,
    house_number: address?.house_number,
    road: address?.road,
    neighbourhood: address?.neighbourhood,
    suburb: address?.suburb,
    city: address?.city || address?.town || address?.village,
    state: address?.state,
    country: address?.country,
    postcode: address?.postcode,
  }

  const formattedAddress =
    locationData?.display_name ||
    [
      buildingName,
      address?.house_number && address?.road && primaryLocation !== address?.road
        ? `${address.house_number} ${address.road}`
        : null,
      primaryLocation,
      address?.city && address?.city !== primaryLocation ? address.city : null,
      address?.state,
    ]
      .filter(Boolean)
      .join(', ')

  return {
    ...locationData,
    display_name: enhancedDisplayName,
    formatted_address: formattedAddress,
    building_name: buildingName,
    primary_location: primaryLocation,
    address_components: addressComponents,
    source: DEFAULT_PROVIDER,
  }
}

/**
 * Determines the type of building/location
 * @param {Object} address - Address components from Nominatim
 * @param {Object} extratags - Extra tags from OSM
 * @returns {string} Building/location type
 */
const getBuildingType = (address, extratags) => {
  // Check for specific amenity types
  if (address?.amenity) {
    return address.amenity
  }

  // Check building type from extratags
  if (extratags?.building && extratags.building !== 'yes') {
    return extratags.building
  }

  // Check for office, shop, etc.
  const locationTypes = ['office', 'shop', 'restaurant', 'hotel', 'hospital', 'school', 'university']
  for (const type of locationTypes) {
    if (address?.[type]) {
      return type
    }
  }

  return 'building'
}

/**
 * Determines the accuracy level of the geocoding result
 * @param {Object} locationData - Nominatim response data
 * @returns {string} Accuracy level
 */
const getAccuracyLevel = (locationData) => {
  const { address, extratags, namedetails } = locationData

  // Building level accuracy (highest)
  if (extratags?.name || extratags?.building || namedetails?.name) {
    return 'building' // Specific building identified
  }

  if (address?.amenity || address?.building || address?.office ||
    address?.shop || address?.restaurant || address?.hotel ||
    address?.hospital || address?.school || address?.university) {
    return 'building' // Specific amenity/building type
  }

  // Address level accuracy
  if (address?.house_number && address?.road) {
    return 'address' // House number + street
  }

  // Neighborhood level accuracy
  if (address?.neighbourhood || address?.suburb || address?.residential ||
    address?.commercial || address?.hamlet || address?.quarter) {
    return 'neighborhood' // Specific neighborhood/area
  }

  // Street level accuracy
  if (address?.road && !address.road.includes('Jalan')) {
    return 'street' // Named street (not generic "Jalan")
  }

  // City level accuracy
  if (address?.city || address?.town || address?.village) {
    return 'city' // City/town level
  }

  // Municipality level accuracy
  if (address?.municipality) {
    return 'municipality' // Municipality level
  }

  return 'general' // General area accuracy
}

export const getAttendanceDashboard = async (startDate, endDate) => {
  const response = await axios.get(`/attendance?start_date=${startDate}&end_date=${endDate}`)
  return getData(response)
}

export const searchLocations = async ({ query, latitude, longitude, limit = 10 }) => {
  const params = {
    q: query,
    limit,
  }

  if (latitude != null && longitude != null) {
    params.lat = latitude
    params.lon = longitude
  }

  const response = await axios.get('/geocoding/search', { params })
  return getData(response)
}

export const postClockIn = async (data) => {
  const response = await axios.post(`/attendance/clockin`, data)
  return getData(response)
}

export const postClockOut = async (data) => {
  const response = await axios.post(`/attendance/clockout`, data)
  return getData(response)
}

export const postPauseAttendance = async (payload = {}) => {
  const response = await axios.post(`/attendance/pause`, payload)
  return getData(response)
}

export const postResumeAttendance = async (payload = {}) => {
  const response = await axios.post(`/attendance/resume`, payload)
  return getData(response)
}

export const getAllDailyAttendance = async (date, page, total, search) => {
  const response = await axios.get(
    `/admin/attendance/daily?date=${date}&page=${page + 1}&total=${total}&search=${search}`
  )
  return getData(response)
}

export const getAllCalendarAttendance = async (start_date, end_date, page, total, search) => {
  const response = await axios.get(
    `/admin/attendance/calendar?start_date=${start_date}&end_date=${end_date}&page=${page + 1
    }&total=${total}&search=${search}`
  )
  return getData(response)
}

export const getAllAttendanceIndividual = async (id, start_date, end_date, page, total) => {
  const response = await axios.get(
    `/admin/attendance/${id}?start_date=${start_date}&end_date=${end_date}&page=${page + 1
    }&total=${total}`
  )
  return getData(response)
}

export const getAllAttendanceIndividualBiometric = async (id, start_date, end_date) => {
  const response = await axios.get(
    `/verification-logs?staff_id=${id}&start_date=${start_date}&end_date=${end_date}`
  )
  return getData(response)
}

/**
 * Get TomTom API usage statistics for debugging
 */
export const getTomTomUsage = () => {
  const today = new Date().toDateString()
  const usageKey = `tomtom_usage_${today}`
  const callsToday = parseInt(safeStorageGet(usageKey, '0'), 10)

  return {
    callsToday,
    dailyLimit: TOMTOM_DAILY_LIMIT,
    remaining: TOMTOM_DAILY_LIMIT - callsToday,
    percentage: Math.round((callsToday / TOMTOM_DAILY_LIMIT) * 100),
    date: today,
    hasBackendProxy: BACKEND_GEOCODING_AVAILABLE,
    backendUrl: process.env.REACT_APP_API_URL,
  }
}

/**
 * Reset TomTom usage counter (for testing purposes)
 */
export const resetTomTomUsage = () => {
  const today = new Date().toDateString()
  const usageKey = `tomtom_usage_${today}`
  localStorage.removeItem(usageKey)
  tomtomCallsToday = 0
  console.log('🗺️ TomTom usage counter reset')
}

/**
 * Log current geocoding service status
 */
export const getGeocodingStatus = () => {
  const tomtomUsage = getTomTomUsage()

  // Get cache details
  const cacheEntries = Array.from(locationCache.entries()).map(([key, data]) => ({
    key,
    timestamp: new Date(data.timestamp).toLocaleTimeString(),
    ageMinutes: Math.round((Date.now() - data.timestamp) / 60000),
    isValid: isCacheValid(data),
    coordinates: key
  }))

  console.log('🗺️ Geocoding Service Status:')
  console.log(`📊 TomTom: ${tomtomUsage.callsToday}/${tomtomUsage.dailyLimit} calls (${tomtomUsage.percentage}%)`)
  console.log(`🔗 Backend Proxy: ${tomtomUsage.hasBackendProxy ? 'Available' : 'Not Available'}`)
  console.log(`🌐 Backend URL: ${tomtomUsage.backendUrl}`)
  console.log(`💾 Cache entries: ${locationCache.size}`)
  console.log('📋 Cache Details:', cacheEntries)
  console.log(`⚙️ Cache Settings: ${CACHE_DURATION / 60000}min duration, ±${CACHE_PRECISION}° precision`)

  return {
    ...tomtomUsage,
    cache: {
      size: locationCache.size,
      entries: cacheEntries,
      durationMinutes: CACHE_DURATION / 60000,
      precisionDegrees: CACHE_PRECISION
    }
  }
}

/**
 * Clear location cache for testing
 */
export const clearLocationCache = () => {
  const cacheSize = locationCache.size
  locationCache.clear()
  console.log(`🗑️ Cleared ${cacheSize} cache entries`)
  return { clearedEntries: cacheSize }
}

// Expose debug functions to window for easy testing
if (typeof window !== 'undefined') {
  window.getTomTomUsage = getTomTomUsage
  window.resetTomTomUsage = resetTomTomUsage
  window.getGeocodingStatus = getGeocodingStatus
  window.clearLocationCache = clearLocationCache
}

export default getLocation

export const getAllShifts = async () => {
  const response = await axios.get(`/admin/shift`)
  return getData(response)
}

export const postCreateShift = async (data) => {
  const response = await axios.post(`/admin/shift`, data)
  return getData(response)
}

export const getShiftDetails = async (shiftId) => {
  const response = await axios.get(`/admin/shift/${shiftId}`)
  return getData(response)
}

export const patchActivateDeactivateShift = async (shiftId, data) => {
  const response = await axios.patch(`/admin/shift/${shiftId}/status`, data)
  return getData(response)
}

export const getShiftChangeImpact = async (shiftId) => {
  const response = await axios.get(`/admin/shift/${shiftId}/change-impact`)
  return getData(response)
}

export const putEditShift = async (shiftId, data) => {
  const response = await axios.put(`/admin/shift/${shiftId}`, data)
  return getData(response)
}

export const deleteShift = async (shiftId) => {
  const response = await axios.delete(`/admin/shift/${shiftId}`)
  return getData(response)
}

export const getActiveEmployees = async () => {
  const response = await axios.get(`/active-staff-list`)
  return getData(response)
}

export const exportAttendanceToCsv = async (exportData) => {
  const response = await axios.post(`/admin/attendance/export`, exportData, {
    responseType: 'blob'
  })
  return response
}

export const exportIndividualAttendanceToCsv = async (staffId, exportData) => {
  const response = await axios.post(`/admin/attendance/export/${staffId}`, exportData, {
    responseType: 'blob'
  })
  return response
}

/**
 * Fetches TomTom Static Map Image
 */
export const fetchTomTomMapImage = async (latitude, longitude, width = 400, height = 300, zoom = 15) => {
  const response = await axios.get('/geocoding/tomtom/map', {
    params: { lat: latitude, lon: longitude, width, height, zoom },
    timeout: 15000,
  })

  const result = getData(response)
  return normalizeMapResponse(result, { provider: 'tomtom', width, height, zoom })
}

/**
 * Fetches OpenStreetMap Static Map Image
 */
export const fetchOSMMapImage = async (latitude, longitude, width = 400, height = 300, zoom = 15) => {
  const response = await axios.get('/geocoding/osm/map', {
    params: { lat: latitude, lon: longitude, width, height, zoom },
    timeout: 10000,
  })

  const result = getData(response)
  return normalizeMapResponse(result, { provider: 'openstreetmap', width, height, zoom })
}

/**
 * Fetches map image with fallback (TomTom first, then OSM)
 */
export const fetchMapImage = async (
  latitude,
  longitude,
  width = 400,
  height = 300,
  zoom = 15,
  options = {}
) => {
  const provider = (options.provider || DEFAULT_MAP_PROVIDER).toLowerCase()

  if (provider === 'tomtom') {
    return fetchTomTomMapImage(latitude, longitude, width, height, zoom)
  }

  return fetchOSMMapImage(latitude, longitude, width, height, zoom)
}


